import { SectionCard } from 'components/GridItemList';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import useAuth from 'hooks/useAuth';
import { useContext } from 'react';
import type { DraftRight } from 'types/drafts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { nameInitials, useEmployeeNameFormatter } from 'utils/nameOrder';
import { targetAbout } from './DraftShareBody';
import { DraftActionsContext } from './useDraftActions';

const RIGHTS: Record<DraftRight, string> = {
    VIEW: 'draft.share.view',
    EDIT: 'draft.share.edit',
    OWNER: 'draft.share.owner',
};

/**
 * Whom the page's draft (`DraftActionsContext`) is shared with, beside its fields: its creator, then each person and
 * team with their access. Its owner manages them from here, in the share dialog.
 */
export function DraftSharedWith() {
    const isNew = useIsNewDesign();
    const { user } = useAuth();
    const actions = useContext(DraftActionsContext);
    const formatName = useEmployeeNameFormatter();
    const draft = actions?.draft;
    if (!actions || !draft) return null;

    const { owner } = draft;
    const rows = [
        ...(owner
            ? [
                  {
                      key: 'owner',
                      initials: nameInitials(owner),
                      name:
                          owner.id === user?.id ? t('draft.share.you', { name: formatName(owner) }) : formatName(owner),
                      about: t('draft.share.creator'),
                      right: 'OWNER' as DraftRight,
                      isTeam: false,
                  },
              ]
            : []),
        ...draft.shares.map((share) => ({
            key: `${share.type}-${share.id}`,
            initials: nameInitials(share),
            name: formatName(share),
            about: targetAbout(share, 'draft.record.team'),
            right: share.right,
            isTeam: share.type === 'TEAM',
        })),
    ];
    const manage = actions.canShare ? (
        <button
            type="button"
            onClick={actions.share}
            className="text-link new:text-primary-ink new:text-meta new:font-semibold cursor-pointer text-sm font-medium hover:underline"
        >
            {t('draft.share.manage')}
        </button>
    ) : null;

    if (isNew) {
        return (
            <SectionCard title={t('draft.shared-with')} action={manage} className="gap-1.5 pb-3">
                <ul className="m-0 list-none p-0">
                    {rows.map((row) => (
                        <li key={row.key} className="border-divider-soft flex items-center gap-2.5 border-t py-2">
                            <span
                                aria-hidden
                                className={cn(
                                    'text-primary-ink text-caption flex size-7 shrink-0 items-center justify-center rounded-full font-semibold',
                                    row.isTeam ? 'bg-neutral-tint' : 'bg-primary-tint'
                                )}
                            >
                                {row.initials}
                            </span>
                            <span className="flex min-w-0 flex-[1_1_8rem] flex-col">
                                <span className="truncate">{row.name}</span>
                                <span className="text-caption text-muted-foreground">{row.about}</span>
                            </span>
                            <span className="text-meta text-foreground-secondary">{t(RIGHTS[row.right])}</span>
                        </li>
                    ))}
                </ul>
            </SectionCard>
        );
    }

    return (
        <section className="border-divider bg-card new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-[10px] border">
            <div className="border-divider flex items-center gap-3 border-b px-4 py-2">
                <h2 className="my-1 min-w-0 flex-auto text-base leading-6 font-semibold">{t('draft.shared-with')}</h2>
                {manage}
            </div>
            <ul className="m-0 list-none px-4 py-0">
                {rows.map((row) => (
                    <li
                        key={row.key}
                        className="border-divider flex items-center gap-2.5 border-b py-3 text-sm last:border-b-0"
                    >
                        <span className="flex min-w-0 flex-auto flex-col gap-0.5">
                            <span className="text-link">{row.name}</span>
                            <span className="text-fg-secondary text-[13px]">{row.about}</span>
                        </span>
                        <span className="text-grey-a400">{t(RIGHTS[row.right])}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}
